import { UserRound } from "lucide-react";
import { Fragment, useRef, type FC } from "react";

import {
  TEMPLATE_FIRST_NAME_TOKEN,
  type OutgoingIntakeReminderSchedule,
  type OutgoingIntakeTemplateDefaults
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Tag } from "@/components/ui/tag";
import { Textarea } from "@/components/ui/textarea";
import {
  CustomReminders,
  ReminderPresetSelect,
  useReminderCadence
} from "@/features/requestsIde/editor/ReminderCadence";
import { EXPIRY_DAYS } from "@/features/requestsIde/editor/reminders";
import {
  DEFAULT_EXPIRY_DAYS,
  DEFAULT_REMINDERS
} from "@/features/requestsIde/editor/useIntakeDraft";
import { WelcomeVideoCard } from "@/features/requestsIde/editor/welcomeVideo/WelcomeVideoCard";

import type { TemplateWelcomeVideo } from "../hooks/useTemplateWelcomeVideo";
import { storedReminders, templateReminders } from "../intakeTemplates";

const STANDARD = "standard";

/** The note with First Name shown as a chip, as every client will read it. */
export const NotePreview: FC<{ note: string }> = ({ note }) => {
  const parts = note.split(TEMPLATE_FIRST_NAME_TOKEN);
  return (
    <span className="text-sm whitespace-pre-line text-fg-3">
      {parts.map((part, i) => (
        // The parts are positional: the same text can repeat.
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={i}>
          {i > 0 ? (
            <Tag className="mx-0.5 align-baseline">First Name</Tag>
          ) : null}
          {part}
        </Fragment>
      ))}
    </span>
  );
};

/** The note every intake starts with, with Insert Client Name. */
export const TemplateNoteField: FC<{
  id: string;
  value: string;
  onChange: (note: string) => void;
  readOnly?: boolean;
  rows?: number;
}> = ({ id, value, onChange, readOnly, rows = 3 }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  /** Where the lawyer last left the caret; the end until they've been in. */
  const caret = useRef<[number, number] | null>(null);
  const insertName = () => {
    const el = ref.current;
    const [start, end] = caret.current ?? [value.length, value.length];
    const next =
      value.slice(0, start) + TEMPLATE_FIRST_NAME_TOKEN + value.slice(end);
    onChange(next);
    const after = start + TEMPLATE_FIRST_NAME_TOKEN.length;
    caret.current = [after, after];
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(after, after);
    });
  };
  return (
    <Field>
      <div className="flex items-center">
        <FieldLabel htmlFor={id} className="flex-1 text-sm">
          Note to the Client
        </FieldLabel>
        {readOnly ? null : (
          <Button variant="ghost" size="xs" onClick={insertName}>
            <UserRound /> Insert Client Name
          </Button>
        )}
      </div>
      <Textarea
        id={id}
        ref={ref}
        value={value}
        rows={rows}
        disabled={readOnly}
        placeholder="Shown at the top of the form and in the email. Why you're asking and how long it takes."
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => {
          caret.current = [
            e.currentTarget.selectionStart,
            e.currentTarget.selectionEnd
          ];
        }}
        className="min-h-0"
      />
      {value.includes(TEMPLATE_FIRST_NAME_TOKEN) ? (
        <NotePreview note={value} />
      ) : (
        <FieldDescription className="text-sm">
          Insert Client Name to greet each client by first name.
        </FieldDescription>
      )}
    </Field>
  );
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults: OutgoingIntakeTemplateDefaults;
  onChange: (patch: OutgoingIntakeTemplateDefaults) => void;
  video: TemplateWelcomeVideo;
  readOnly?: boolean;
  /** Use a Firm Video: the firm's library. */
  onLibrary?: () => void;
};

/**
 * "Every New Intake Starts With": the note, welcome video, link expiry,
 * reminders and branding each intake made from the template begins with.
 * The request can still change any of them. Edits save with the template.
 */
export const TemplateDefaultsSheet: FC<Props> = ({
  open,
  onOpenChange,
  defaults,
  onChange,
  video,
  readOnly,
  onLibrary
}) => {
  const reminders = templateReminders(
    defaults.reminder_schedule,
    DEFAULT_REMINDERS
  );
  const setReminders = (next: OutgoingIntakeReminderSchedule | null) =>
    onChange({ reminder_schedule: storedReminders(next) });
  const cadence = useReminderCadence(reminders, setReminders);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-110 flex-col gap-0 data-[side=right]:sm:max-w-110"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="template-defaults-sheet"
      >
        <SheetHeader>
          <SheetTitle>Every New Intake Starts With</SheetTitle>
          <SheetDescription>
            Each intake made from this template begins with these. The request
            can still change them; intakes already sent keep theirs.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
          <TemplateNoteField
            id="template-defaults-note"
            value={defaults.attorney_note ?? ""}
            readOnly={readOnly}
            onChange={(note) => onChange({ attorney_note: note || null })}
          />

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-2">Welcome Video</span>
            <WelcomeVideoCard
              videoUrl={video.url}
              upload={video.upload}
              who="every client"
              readOnly={readOnly}
              onRecord={() => video.setRecording(true)}
              onUpload={video.uploadFile}
              onRemove={() => void video.remove()}
              onLibrary={onLibrary}
              libraryLabel="Use a Firm Video"
            />
          </div>

          <FieldGroup className="gap-4">
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel className="text-sm">Link Expires After</FieldLabel>
                <Select
                  value={
                    defaults.expires_in_days
                      ? String(defaults.expires_in_days)
                      : STANDARD
                  }
                  disabled={readOnly}
                  onValueChange={(v) =>
                    onChange({
                      expires_in_days: v === STANDARD ? null : Number(v)
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className="w-full"
                    aria-label="Link Expires After"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={STANDARD}>
                      Standard ({DEFAULT_EXPIRY_DAYS} days)
                    </SelectItem>
                    {EXPIRY_DAYS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <ReminderPresetSelect
                dense={false}
                value={reminders}
                presetId={cadence.presetId}
                onChoose={cadence.choose}
                disabled={readOnly}
              />
            </div>
            {cadence.custom ? (
              <CustomReminders
                dense={false}
                value={reminders}
                onChange={setReminders}
                disabled={readOnly}
              />
            ) : null}
          </FieldGroup>

          <Field orientation="horizontal">
            <Switch
              checked={Boolean(defaults.enable_firm_branding)}
              disabled={readOnly}
              onCheckedChange={(v) => onChange({ enable_firm_branding: v })}
              aria-label="Show the Firm's Logo"
            />
            <div>
              <FieldLabel className="text-sm">
                Show the Firm&apos;s Logo
              </FieldLabel>
              <FieldDescription className="text-sm">
                Clients see your logo and colors instead of CaseBlink&apos;s.
              </FieldDescription>
            </div>
          </Field>
        </div>
        <SheetFooter className="flex-row justify-end border-t border-line-1">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
